/**
 * The cumulative board's data fetching.
 *
 * Separate from `useLeaderboard` rather than folded into it: that hook is keyed
 * on a selected run and refetches whenever the run changes, and the board has no
 * run. Sharing one hook would mean every run click refetching a board that did
 * not change.
 *
 * `enabled` is passed by the caller so the fetch does not fire until the view is
 * actually shown — the board is the second tab, and loading it on mount would
 * cost a request per page load for a view most visits never open.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  getBoardEntry,
  listBoard,
  retireBoardTarget,
  retireRun,
  type TokenGetter,
} from '../api/leaderboard'
import type {
  BoardEntry,
  BoardObservation,
  BoardSortColumn,
  BoardTierFloor,
  SortDirection,
} from '../api/types'
import { DEFAULT_PAGE_SIZE } from '../api/types'

export interface BoardState {
  entries: BoardEntry[]
  total: number
  sort: BoardSortColumn
  direction: SortDirection
  minRuns: number
  /**
   * The tier floor, or `null` for the whole board. A floor and not an exact
   * match, and satisfied by either `latest_tier` or `best_tier` server-side —
   * see the `/board` route, which is where that rule lives.
   */
  tier: BoardTierFloor | null
  /** Rows per page, and the index of the first row shown. */
  limit: number
  offset: number
  loading: boolean
  error: string | null
  unauthorized: boolean
  /** The API's own caveat on `runs_identified`, rendered rather than restated. */
  note: string | null
  /**
   * The API's caveat on the champions columns. Null on a runtime that predates
   * them — which is why it is not defaulted to a hardcoded sentence here: a
   * restated caveat is one that can drift from the one the server ships.
   */
  championsNote: string | null
  /** Per-target observation history, loaded on demand when a row is expanded. */
  history: Record<string, BoardObservation[]>
  /** `target_key` whose retirement is in flight, or `null`. */
  retiring: string | null
  /** Why the last retirement failed. Cleared when the next one starts. */
  retireError: string | null
  /**
   * The API's own sentence about the last successful retirement — for the run
   * variant it carries the count, and `0` is a success worth reporting rather than
   * hiding, because it means that run put nothing on the board.
   */
  retireNotice: string | null
  /**
   * Retire one board row. Resolves `true` when the row was actually retired, so the
   * caller can keep its dialog open on a failure instead of closing over the error.
   *
   * The justification is required by the API. Callers disable confirm on an empty
   * one rather than letting the 422 be the feedback.
   */
  retire: (targetKey: string, justification: string) => Promise<boolean>
  /**
   * Retire every board row a run contributed. Same contract. Offered beside the
   * per-target action because that is where the contaminated row is noticed: a
   * testing sweep put dozens of rows on the board and retiring them individually is
   * dozens of typed reasons for one decision.
   */
  retireRunRows: (runId: string, justification: string) => Promise<boolean>
  toggleSort: (column: BoardSortColumn) => void
  setMinRuns: (n: number) => void
  setTier: (tier: BoardTierFloor | null) => void
  setOffset: (n: number) => void
  setPageSize: (n: number) => void
  loadHistory: (targetKey: string) => void
}

/**
 * Just the retire slice, for the table and drawer that offer the action.
 *
 * A `Pick` of `BoardState` rather than its own object, so the caller passes the whole
 * board state and no new object is built per render — and so the two cannot drift.
 */
export type BoardRetire = Pick<
  BoardState,
  'retiring' | 'retireError' | 'retire' | 'retireRunRows'
>

export function useBoard(getToken: TokenGetter, enabled: boolean): BoardState {
  const [entries, setEntries] = useState<BoardEntry[]>([])
  const [total, setTotal] = useState(0)
  const [sort, setSort] = useState<BoardSortColumn>('runs_identified')
  const [direction, setDirection] = useState<SortDirection>('desc')
  const [minRuns, setMinRunsState] = useState(1)
  const [tier, setTierState] = useState<BoardTierFloor | null>(null)
  const [limit, setLimit] = useState(DEFAULT_PAGE_SIZE)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [championsNote, setChampionsNote] = useState<string | null>(null)
  const [history, setHistory] = useState<Record<string, BoardObservation[]>>({})
  const [retiring, setRetiring] = useState<string | null>(null)
  const [retireError, setRetireError] = useState<string | null>(null)
  const [retireNotice, setRetireNotice] = useState<string | null>(null)
  // Bumped after a retirement to refetch the page. Not a local row filter: the
  // retired row leaving the page changes `total` too, and `Pagination` reads it —
  // dropping the row locally would leave the header claiming one more entry than
  // the list holds, and page 2's contents shifted by one.
  const [reloadKey, setReloadKey] = useState(0)

  const handleError = useCallback((e: unknown) => {
    if (e instanceof DOMException && e.name === 'AbortError') return
    if (e instanceof ApiError && e.status === 401) {
      setUnauthorized(true)
      return
    }
    setError(e instanceof Error ? e.message : String(e))
  }, [])

  useEffect(() => {
    if (!enabled) return
    const ac = new AbortController()
    setLoading(true)

    void (async () => {
      try {
        const page = await listBoard(
          { sort, direction, minRuns, tier, limit, offset },
          getToken,
          ac.signal,
        )
        if (ac.signal.aborted) return
        setEntries(page.entries)
        setTotal(page.total)
        setNote(page.runs_identified_note)
        setChampionsNote(page.champions_corroboration_note ?? null)
        setError(null)
      } catch (e) {
        handleError(e)
      } finally {
        if (!ac.signal.aborted) setLoading(false)
      }
    })()

    return () => ac.abort()
  }, [
    enabled,
    sort,
    direction,
    minRuns,
    tier,
    limit,
    offset,
    reloadKey,
    getToken,
    handleError,
  ])

  const toggleSort = useCallback(
    (column: BoardSortColumn) => {
      // Every re-ordering and every filter change goes back to page 1. Keeping
      // the offset would page into a different set of rows than the one on
      // screen, so "page 3" would silently mean something else after a sort.
      setOffset(0)
      if (sort === column) {
        setDirection(direction === 'asc' ? 'desc' : 'asc')
        return
      }
      setSort(column)
      // Everything except the two text columns is "more/higher is interesting",
      // and tier strength is sorted by position rather than alphabetically in
      // SQL, so descending there means strongest first.
      setDirection(column === 'protein' || column === 'pathway' ? 'asc' : 'desc')
    },
    [sort, direction],
  )

  const setMinRuns = useCallback((n: number) => {
    setOffset(0)
    setMinRunsState(n)
  }, [])

  // Back to page 1 for the reason a re-sort is: a narrower board has fewer pages,
  // so keeping the offset can land on a page that no longer exists and show an
  // empty table over a filter that matched rows.
  const setTier = useCallback((next: BoardTierFloor | null) => {
    setOffset(0)
    setTierState(next)
  }, [])

  const setPageSize = useCallback((n: number) => {
    setOffset(0)
    setLimit(n)
  }, [])

  // ── Retire ─────────────────────────────────────────────────────────────────
  // Both variants share this, because a retirement fails in exactly two ways that
  // need different words: a 401 is "sign in again" and belongs on the page-level
  // gate, anything else is the API's own sentence and belongs in the dialog that
  // asked. Returning a boolean rather than throwing keeps the caller's dialog in
  // charge of whether to close.
  const runRetire = useCallback(
    async (key: string, work: () => Promise<string>): Promise<boolean> => {
      setRetiring(key)
      setRetireError(null)
      setRetireNotice(null)
      try {
        setRetireNotice(await work())
        // The row is off the default board now, so the page it was on has changed.
        setReloadKey((k) => k + 1)
        return true
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) setUnauthorized(true)
        else setRetireError(e instanceof Error ? e.message : String(e))
        return false
      } finally {
        setRetiring(null)
      }
    },
    [],
  )

  const retire = useCallback(
    (targetKey: string, justification: string) =>
      runRetire(targetKey, async () => {
        const out = await retireBoardTarget(targetKey, justification, getToken)
        // `persisted: false` is a 200 that wrote nothing — no pool. Reported, not
        // celebrated: the row is still on the board and the user needs to know.
        if (out.persisted === false)
          throw new Error(
            'The board store was unreachable, so nothing was retired. Try again.',
          )
        return `Retired ${out.protein ?? targetKey} from the board.`
      }),
    [runRetire, getToken],
  )

  const retireRunRows = useCallback(
    (runId: string, justification: string) =>
      runRetire(runId, async () => {
        const out = await retireRun(runId, justification, getToken)
        if (out.persisted === false)
          throw new Error(
            'The board store was unreachable, so nothing was retired. Try again.',
          )
        // Zero is a success and says something worth reading: that run put nothing
        // on the board, or its rows were already retired.
        return out.retired === 0
          ? `${runId} had no board rows left to retire.`
          : `Retired ${out.retired} board row${out.retired === 1 ? '' : 's'} from ${runId}.`
      }),
    [runRetire, getToken],
  )

  const loadHistory = useCallback(
    (targetKey: string) => {
      // Fetched once per key and kept: the observation log is immutable, so a
      // second fetch could only return the same rows.
      if (history[targetKey]) return
      void (async () => {
        try {
          const detail = await getBoardEntry(targetKey, getToken)
          setHistory((prev) => ({ ...prev, [targetKey]: detail.observations }))
        } catch (e) {
          handleError(e)
        }
      })()
    },
    [history, getToken, handleError],
  )

  return {
    entries,
    total,
    sort,
    direction,
    minRuns,
    tier,
    limit,
    offset,
    loading,
    error,
    unauthorized,
    note,
    championsNote,
    history,
    retiring,
    retireError,
    retireNotice,
    retire,
    retireRunRows,
    toggleSort,
    setMinRuns,
    setTier,
    setOffset,
    setPageSize,
    loadHistory,
  }
}
